import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getCatalogItemHistory } from '../services/api';

// ─── helpers ────────────────────────────────────────────────────────────────

function fmt(n, decimals = 0) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: color || '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: 8,
      padding: '10px 14px',
      minWidth: 120,
      flex: '1 1 120px',
    }}>
      <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#6c757d', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Colour bands for metric cards
function turnoverBg(v)   { if (!v) return undefined; return v >= 4 ? '#e6f4ea' : v >= 1 ? '#fff4e6' : '#ffe6e6'; }
function sellThruBg(v)   { if (!v) return undefined; const p = v * 100; return p >= 50 ? '#e6f4ea' : p >= 20 ? '#fff4e6' : '#ffe6e6'; }
function dosBg(v)        { if (!v) return undefined; return v < 30 ? '#ffe6e6' : v < 60 ? '#fff4e6' : '#e6f4ea'; }
function ageBg(v)        { if (!v) return undefined; return v > 180 ? '#ffe6e6' : v > 90 ? '#fff4e6' : undefined; }

// Render month labels nicely (2025-04 → Apr '25)
function fmtMonth(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(+y, +mo - 1, 1);
  return d.toLocaleString('default', { month: 'short' }) + " '" + String(y).slice(2);
}

// Custom recharts tooltip
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #dee2e6',
      borderRadius: 6, padding: '8px 12px', fontSize: '0.8rem',
    }}>
      <strong>{fmtMonth(label)}</strong>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

export default function CatalogItemModal({ item, metrics: m, onHide }) {
  const [history, setHistory]     = useState(null);
  const [histLoading, setHistLoading] = useState(true);
  const [histError, setHistError] = useState(null);

  useEffect(() => {
    if (!item?.idCatalog) return;
    setHistLoading(true);
    setHistError(null);
    getCatalogItemHistory(item.idCatalog)
      .then(data => setHistory(data))
      .catch(() => setHistError('Failed to load history.'))
      .finally(() => setHistLoading(false));
  }, [item?.idCatalog]);

  const turns     = m ? Number(m.turnover_annual  ?? 0) : null;
  const sellThru  = m ? Number(m.sell_through_90  ?? 0) : null;
  const dos       = m?.days_of_supply != null ? Number(m.days_of_supply) : null;
  const avgAge    = m ? Number(m.weighted_avg_age ?? 0) : null;
  const avgOrd    = m?.avg_order_qty != null ? Number(m.avg_order_qty) : null;
  const avgDays   = m?.avg_days_between_orders != null ? Number(m.avg_days_between_orders) : null;

  // Build chart data — fill any missing months so the x-axis is continuous
  const chartData = React.useMemo(() => {
    if (!history?.monthly) return [];
    return history.monthly.map(row => ({
      month:      row.month,
      label:      fmtMonth(row.month),
      Inbound:    Number(row.inbound  || 0),
      Outbound:   Number(row.outbound || 0),
      Orders:     Number(row.order_count || 0),
    }));
  }, [history]);

  // Lot table
  const lots = history?.lots || [];
  const totalOnHand  = lots.reduce((s, l) => s + Number(l.onHand || 0), 0);
  const totalAvail   = lots.reduce((s, l) => s + Number(l.availQty || 0), 0);

  return (
    <Modal show onHide={onHide} size="xl" dialogClassName="catalog-item-modal-xl">
      <Modal.Header closeButton style={{ borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title style={{ fontSize: '1rem' }}>
          <span style={{ color: '#6c757d', marginRight: 8 }}>{item.itemNumber}</span>
          {item.itemName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>

        {/* ── Metric summary cards ───────────────────────────────── */}
        {m && m !== 'loading' && m !== 'error' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            <MetricCard label="On Hand"      value={fmt(m.current_inventory)} />
            <MetricCard label="Avg Age"      value={avgAge ? `${avgAge}d` : '—'}
                        sub="qty-weighted"   color={ageBg(avgAge)} />
            <MetricCard label="Days Supply"  value={dos != null ? `${dos}d` : '—'}
                        sub="at current rate" color={dosBg(dos)} />
            <MetricCard label="Turns / Yr"   value={turns != null ? `${turns.toFixed(2)}×` : '—'}
                        sub="annualized"     color={turnoverBg(turns)} />
            <MetricCard label="Sell-Thru 90d" value={sellThru != null ? `${(sellThru*100).toFixed(1)}%` : '—'}
                        sub={`${fmt(m.issued_90_days)} issued`} color={sellThruBg(sellThru)} />
            <MetricCard label="Avg Ord Qty"  value={avgOrd != null ? fmt(avgOrd, 1) : '—'}
                        sub={`${fmt(m.order_count_90)} orders / 90d`} />
            <MetricCard label="Avg Days/Ord" value={avgDays != null ? `${avgDays}d` : '—'}
                        sub="between orders" />
            <MetricCard label="Avg Inventory" value={fmt(m.avg_inventory, 0)}
                        sub="(begin+end)/2" />
          </div>
        )}

        {/* ── History chart ──────────────────────────────────────── */}
        <h6 style={{ fontWeight: 600, marginBottom: 8 }}>12-Month Inbound / Outbound</h6>

        {histLoading && (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        )}
        {histError && <Alert variant="danger" className="mb-3">{histError}</Alert>}

        {!histLoading && !histError && chartData.length === 0 && (
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>No transaction history in the last 12 months.</p>
        )}

        {!histLoading && !histError && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="qty"  orientation="left"  tick={{ fontSize: 11 }}
                     label={{ value: 'Units', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }} />
              <YAxis yAxisId="ord"  orientation="right" tick={{ fontSize: 11 }} width={36}
                     label={{ value: 'Orders', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11 } }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar  yAxisId="qty" dataKey="Inbound"  fill="#4a9ded" radius={[3,3,0,0]} />
              <Bar  yAxisId="qty" dataKey="Outbound" fill="#f07b3f" radius={[3,3,0,0]} />
              <Line yAxisId="ord" dataKey="Orders"   stroke="#7c5cbf" dot={{ r: 3 }}
                    strokeWidth={2} type="monotone" />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {/* ── Lot breakdown table ────────────────────────────────── */}
        {!histLoading && lots.length > 0 && (
          <>
            <h6 style={{ fontWeight: 600, margin: '18px 0 8px' }}>
              Active Lots
              <Badge bg="secondary" style={{ marginLeft: 8, fontWeight: 500, fontSize: '0.7rem' }}>
                {lots.length}
              </Badge>
            </h6>
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto', 
              overflowX: 'auto', 
              borderRadius: 6, 
              border: '1px solid #dee2e6',
              position: 'relative'
            }}>
              <table style={{
                width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                fontSize: '0.8rem', tableLayout: 'auto',
              }}>
                <thead>
                  <tr>
                    {['Lot ID','Received','Age','On Hand','Available','PPU','TAP','Location','Exp Date','Notes'].map(h => (
                      <th key={h} style={{
                        padding: '6px 10px', textAlign: h === 'Lot ID' || h === 'Location' || h === 'Notes' ? 'left' : 'right',
                        backgroundColor: '#f8f9fa', whiteSpace: 'nowrap',
                        boxShadow: 'inset 0 -2px 0 #dee2e6, inset 0 1px 0 #dee2e6',
                        position: 'sticky', top: 0, zIndex: 10,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lots.map((lot, i) => (
                    <tr key={lot.idLevels}
                        style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6' }}>{lot.idLevels}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {lot.recDate ? new Date(lot.recDate).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right',
                                   backgroundColor: lot.ageInDays > 180 ? '#ffe6e6' : lot.ageInDays > 90 ? '#fff4e6' : 'transparent' }}>
                        {lot.ageInDays != null ? `${lot.ageInDays}d` : '—'}
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{fmt(lot.onHand)}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{fmt(lot.availQty)}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>{fmt(lot.ppu, 2)}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>
                        {fmt(Number(lot.availQty || 0) * Number(lot.ppu || 0))}
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                        {lot.warehouseName || '—'}
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {lot.expDate ? new Date(lot.expDate).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #dee2e6', maxWidth: 200,
                                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lot.notes || '—'}
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr style={{ 
                    backgroundColor: '#e9ecef', 
                    fontWeight: 600,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10
                  }}>
                    <td style={{ padding: '5px 10px', boxShadow: 'inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6', backgroundColor: '#e9ecef' }} colSpan={3}>Totals</td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', boxShadow: 'inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6', backgroundColor: '#e9ecef' }}>{fmt(totalOnHand)}</td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', boxShadow: 'inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6', backgroundColor: '#e9ecef' }}>{fmt(totalAvail)}</td>
                    <td style={{ boxShadow: 'inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6', backgroundColor: '#e9ecef' }} colSpan={5} />
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Disclaimer ─────────────────────────────────────────── */}
        <p style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: 16, lineHeight: 1.5 }}>
          <strong>Disclaimer:</strong> On-hand counts are provided as-is. All derived analytics, performance
          metrics, and sell-through calculations are statistical estimates only. [Company Name] makes no
          guarantees regarding future inventory velocity or performance based on these metrics. Independently
          verify trends before making purchasing or ordering commitments.
        </p>

      </Modal.Body>
    </Modal>
  );
}
